import React, {MouseEventHandler, useCallback} from 'react';

import {cls} from '../../../utils';
import {SmallLoader} from '../../small-loader';
import {isIconElement} from '../../svg-icon/icon';
import NextLink from 'next/link';

import {Link, RouteLinkProps} from '../../../../lib/routes';

import css from './button.module.css';

export type ButtonAppearance =
	| 'primary'
	| 'secondary'
	| 'outline'
	| 'subtle'
	| 'transparent'
	| 'destructive'
	| 'success';
export type ButtonSize = 'small' | 'medium' | 'large';
export type ButtonShape = 'rounded' | 'circular' | 'square';

export type ButtonProps = Omit<
	React.ButtonHTMLAttributes<HTMLButtonElement> & React.AnchorHTMLAttributes<HTMLAnchorElement>,
	keyof RouteLinkProps
> &
	RouteLinkProps & {
		appearance?: ButtonAppearance;
		size?: ButtonSize;
		shape?: ButtonShape;
		/** иконка рядом с подписью; если подписи нет, кнопка становится квадратной — «только иконка» */
		icon?: React.ReactNode;
		iconPosition?: 'before' | 'after';
		/** текстовая ссылка вместо кнопки: перебивает appearance */
		link?: boolean;
		checked?: boolean;
		/** подпись переносится на новые строки; по умолчанию в одну строку с многоточием */
		wrap?: boolean;
		disabled?: boolean;
		onClick?: (event: React.MouseEvent) => void;
		className?: string;
		loading?: boolean;
		setRef?: React.RefObject<HTMLButtonElement | HTMLAnchorElement | null>;
	};

export const Button: React.FC<ButtonProps> = (p) => {
	const {
		target,
		href,
		download,
		children,
		onClick,
		appearance = 'secondary',
		size = 'medium',
		shape = 'rounded',
		icon: iconProps,
		iconPosition = 'before',
		link,
		checked,
		wrap,
		className,
		loading,
		setRef,
		type = 'button',
		title,
		route,
		routeParams,
		...props
	} = p;

	/* Иконка без подписи — режим «только иконка»: кнопка становится квадратной по высоте размера */
	const iconOnly = !!iconProps && !children;

	const clsName = cls(
		css.button,
		css[size],
		shape !== 'rounded' && css[shape],
		link ? css.link : css[appearance],
		iconOnly && css.iconOnly,
		wrap && css.wrap,
		checked && css.checked,
		props.disabled && css.disabled,
		loading && css.loading,
		className && !!css[className] ? css[className] : className,
	);

	const icon = iconProps;
	/*const icon = React.isValidElement(iconProps)
		? React.cloneElement(iconProps as React.ReactElement<{appearance?: string; size?: number}>, {
				appearance: isHovered ? 'filled' : 'regular',
				size: 20,
		  })
		: iconProps;*/

	const iconNode =
		isIconElement(icon) && !icon.props.appearance ? (
			<>
				{React.cloneElement(icon, {className: cls(icon.props.className, css.iconRegular)})}
				{React.cloneElement(icon, {appearance: 'filled', className: cls(icon.props.className, css.iconFilled)})}
			</>
		) : (
			icon
		);

	const content = (
		<>
			{!!icon && iconPosition === 'before' && <span className={css.icon}>{iconNode}</span>}
			{!!children && <span className={css.label}>{children}</span>}
			{!!icon && iconPosition === 'after' && <span className={css.icon}>{iconNode}</span>}
		</>
	);

	if (loading) {
		return (
			<div className={clsName}>
				<div className={css.loader}>
					<SmallLoader theme={appearance === 'primary' && !link ? 'button' : 'normal'} />
				</div>
				<div className={css.loadedContent}>{content}</div>
			</div>
		);
	}

	if (route) {
		return (
			<Link
				route={route as any}
				params={routeParams}
				className={clsName}
				onClick={onClick as any}
				ref={setRef as React.RefObject<HTMLAnchorElement | null>}
				target={target}
				title={title}
				{...props}
			>
				{content}
			</Link>
		);
	}

	if (href) {
		return (
			<NextLink
				href={href}
				download={download}
				target={target}
				rel={props.rel}
				aria-label={props['aria-label']}
				className={clsName}
				onClick={onClick as any}
				tabIndex={props.tabIndex}
				onKeyDown={props.onKeyDown}
				onFocus={props.onFocus}
				onBlur={props.onBlur}
				title={title}
			>
				<span ref={setRef}>{content}</span>
			</NextLink>
		);
	}

	return (
		<button
			title={title}
			type={type}
			className={clsName}
			onClick={onClick}
			ref={setRef as React.RefObject<HTMLButtonElement | null>}
			{...props}
		>
			{content}
		</button>
	);
};
